import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInrichtingen } from '../api/api';
import { fmtId, fmtDatumNL } from '../util/format';
import { chipsVoorDoel, labelVoorDoel } from '../util/doelen';
import { useLijst, ZoekBalk, KopRij, GefilterdChips, FilterPaneel, markeer } from '../components/lijst';
import Klokje from '../components/Klokje';

/* De inrichtingenlijst op het lijstontwerp van 26-08-2026. Rechtstype en
   doelen zijn facetten in het paneel; houder en kenmerk hebben een loepje.

   Twee dingen die er in de lijst hetzelfde uitzien en het niet zijn:

     klaarstaand_vanaf   er staat een melding of wijzigingsbesluit klaar; wat je
                         in deze rij ziet geldt nu en verandert op die datum.
     inwerkingtreding    in de toekomst; deze rij zélf geldt nog niet, en tot die
                         dag geldt het vorige recht (dat in de historie staat).

   Allebei een klokje, want in de lijst is de boodschap dezelfde: hier komt iets
   aan. Het verschil staat in de tooltip. */
const vandaagISO = () => new Date().toISOString().slice(0, 10);

const RECHTSTYPE_LABEL = {
  melding: 'Melding',
  vergunning: 'Vergunning',
};

const rechtstype = (i) => RECHTSTYPE_LABEL[i.rechtstype] || i.rechtstype || '';

export default function Inrichtingen() {
  const [inrichtingen, setInrichtingen] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const lijst = useLijst({
    facetten: [
      { key: 'rechtstype', label: 'Rechtstype', waarde: (i) => [rechtstype(i)] },
      { key: 'doelen', label: 'Doelen', waarde: (i) => i.doelen || [], toon: labelVoorDoel },
    ],
    kopzoek: {
      houder: { label: 'Houder', tekst: (i) => i.houder_naam || '' },
      kenmerk: { label: 'Kenmerk', tekst: (i) => i.gebruiksrecht_kenmerk || '' },
    },
    zoek: (i) => [
      ['id', `${i.inrichting_id} ${fmtId(i.inrichting_id)}`],
      ['houder', i.houder_naam || ''],
      ['kenmerk', i.gebruiksrecht_kenmerk || ''],
    ],
    sorteer: {
      inrichting_id: (i) => i.inrichting_id,
      houder_naam: (i) => i.houder_naam || '',
      rechtstype: rechtstype,
      gebruiksrecht_kenmerk: (i) => i.gebruiksrecht_kenmerk || '',
      doelen: (i) => (i.doelen || []).join(','),
      aantal_putten: (i) => i.aantal_putten ?? 0,
    },
  });

  useEffect(() => { laad(); }, []);

  const laad = async () => {
    setLoading(true);
    try {
      const res = await getInrichtingen();
      setInrichtingen(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  const KOLOMMEN = [
    { key: 'inrichting_id', label: 'Inrichting' },
    { key: 'houder_naam', label: 'Houder', loep: 'houder' },
    { key: 'rechtstype', label: 'Rechtstype' },
    { key: 'gebruiksrecht_kenmerk', label: 'Kenmerk', loep: 'kenmerk' },
    { key: 'doelen', label: 'Doelen' },
    { key: 'aantal_putten', label: 'Putten' },
  ];

  const rijen = lijst.filterEnSorteer(inrichtingen);
  const q = lijst.zoek.trim();

  return (
    <div className="main-inner">
      <div className="page-header">
        <div>
          <h1 className="page-title">Inrichtingen</h1>
          <p className="page-subtitle">
            {inrichtingen.length} inrichtingen
            {lijst.filterActief && ` · ${rijen.length} na filter`}
          </p>
        </div>
      </div>

      <ZoekBalk
        lijst={lijst}
        placeholder="Zoek op houder, kenmerk of inrichtingsnummer"
        metPaneelKnop
      />
      <GefilterdChips lijst={lijst} />

      {loading ? (
        <div className="empty-state"><span className="spinner" /></div>
      ) : inrichtingen.length === 0 ? (
        <div className="empty-state">Geen inrichtingen.</div>
      ) : (
        <div className="table-wrap">
          <table className="table pl-tabel">
            <thead><KopRij lijst={lijst} kolommen={KOLOMMEN} /></thead>
            <tbody>
              {rijen.map(i => {
                const t = lijst.zoekTreffer(i);
                const kz = lijst.kopzoek;
                return (
                  <tr
                    key={i.inrichting_id}
                    className="clickable"
                    onClick={() => navigate(`/beregening/inrichtingen/${i.inrichting_id}`)}
                    style={i.einddatum ? { opacity: 0.6 } : undefined}
                  >
                    <td><span className="mono mono-id">
                      {t.waar === 'id' ? markeer(fmtId(i.inrichting_id), q) : fmtId(i.inrichting_id)}
                    </span></td>
                    <td>
                      {i.houder_naam
                        ? (kz.houder ? markeer(i.houder_naam, kz.houder)
                          : t.waar === 'houder' ? markeer(i.houder_naam, q) : i.houder_naam)
                        : <span className="pl-leeg">–</span>}
                    </td>
                    <td>
                      <span
                        className={`chip chip-clickable ${rechtstype(i) === 'Vergunning' ? 'chip-success' : ''}`}
                        title="Klik om op dit rechtstype te filteren"
                        onClick={(e) => { e.stopPropagation(); lijst.zetFilter('rechtstype', rechtstype(i)); }}
                      >{rechtstype(i)}</span>
                    </td>
                    <td>
                      <span className="mono">
                        {i.gebruiksrecht_kenmerk
                          ? (kz.kenmerk ? markeer(i.gebruiksrecht_kenmerk, kz.kenmerk)
                            : t.waar === 'kenmerk' ? markeer(i.gebruiksrecht_kenmerk, q)
                            : i.gebruiksrecht_kenmerk)
                          : <span className="pl-leeg">–</span>}
                      </span>
                      {(() => {
                        const nogNiet = i.inwerkingtreding && i.inwerkingtreding > vandaagISO();
                        const datum = nogNiet ? i.inwerkingtreding : i.klaarstaand_vanaf;
                        if (!datum) return null;
                        const titel = nogNiet
                          ? `Dit gebruiksrecht gaat pas in op ${fmtDatumNL(datum)}; tot die dag geldt het vorige recht`
                          : `Er staat een wijziging van het gebruiksrecht klaar die ingaat op ${fmtDatumNL(datum)}`;
                        return (
                          <span className="klok-vlag" title={titel}>
                            <Klokje titel={titel} />
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      {/* Hier stond de kale databasewaarde, dus letterlijk
                          "vollegronds_boomteelt" in een chip. Nu het korte
                          label, met de volledige bewoording als title. `beide`
                          wordt in twee chips getekend — dat ís het immers —
                          maar filtert nog steeds op de ene waarde `beide`. */}
                      {(i.doelen && i.doelen.length > 0)
                        ? <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                            {i.doelen.flatMap((d) =>
                              chipsVoorDoel(d).map((c, idx) => (
                                <span
                                  key={`${d}-${idx}`}
                                  className="chip chip-clickable"
                                  title={`${c.titel} — klik om op dit doel te filteren`}
                                  onClick={(e) => { e.stopPropagation(); lijst.zetFilter('doelen', d); }}
                                >{c.label}</span>
                              ))
                            )}
                          </span>
                        : <span className="pl-leeg">–</span>}
                    </td>
                    <td>{i.aantal_putten ?? 0}</td>
                  </tr>
                );
              })}
              {rijen.length === 0 && (
                <tr><td colSpan={KOLOMMEN.length} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: 32 }}>
                  Geen rijen voldoen aan de filter.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <FilterPaneel lijst={lijst} rijen={inrichtingen} />
    </div>
  );
}
